import { ArticleType } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { AutomobileDataEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileDataEntity';
import { AutomobileItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileItem';
import { LastActionsSelector } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/components/LastActionsSelector';
import { CartSelectorOption } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/components/LastCartsSelector/components/CartSelectorOption';
import { RemoveStoredCartDialog } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/components/LastCartsSelector/components/RemoveStoredCartDialog';
import { useLastCartsSelector } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/components/LastCartsSelector/hooks/useLastCartsSelector';
import { LAST_ACTIONS_TIME_SELECTORS } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/constants/LastActionsTimeSelectors';
import { CartRepositoryFactory } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartRepository/CartRepositoryFactory';
import { StoredCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/StoredCart';
import { TimeSelectionHeader } from '@/components/shared/components/LastActionsSelectOptions/components/TimeSelectionHeader';
import { LastActionsSelectOptions } from '@/components/shared/components/LastActionsSelectOptions/LastActionsSelectOptions';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

export type LastCartsSelectorOption = { value: StoredCart | null; display: JSX.Element };

interface LastCartsSelectorProps {
  titleId?: string;
  startIcon?: string;
  onSelected?: (option: StoredCart) => void;
}

export const LastCartsSelector = ({
  titleId = 'LAST_CARTS',
  startIcon = 'shopping_cart',
  onSelected,
}: LastCartsSelectorProps) => {
  const {
    state: { selectedAgent, selectedMerchant },
  } = useApplicationContext();

  if (!selectedAgent) return null;

  const { setSelectedValue } = useLastCartsSelector();
  const { opened, setOpened } = useDialog();
  const [selectedCartId, setSelectedCartId] = useState<string | undefined>(undefined);
  const [storedCarts, setStoredCarts] = useState<StoredCart[]>([]);

  const cartRepository = CartRepositoryFactory.getRepository();

  useEffect(() => {
    removeAutoCartsMissingInCdn();
  }, []);

  const updateStoredCarts = () => {
    const currentStoredCarts = cartRepository.get(selectedAgent.id);
    setStoredCarts(currentStoredCarts);
  };

  const removeAutoCartsMissingInCdn = async () => {
    if (!selectedMerchant) return; // needed to get Merchant's allowed brands

    const storedCarts = cartRepository.get(selectedAgent.id);

    const automobileData = new AutomobileDataEntity(selectedMerchant.id);
    await automobileData.init();

    for (const storedCart of storedCarts) {
      const firstItem = storedCart.cart.items[0];

      const isAuto = firstItem.data.type === ArticleType.AUTOMOBILE;

      if (!isAuto) {
        break;
      }

      const isAutoCartValid = await AutomobileItem.isInCdn(firstItem as AutomobileItem, automobileData);

      if (!isAutoCartValid) {
        cartRepository.remove(selectedAgent.id, storedCart.id);
      }
    }

    updateStoredCarts();
  };

  return (
    <>
      <LastActionsSelector
        setSelectedValue={(option: StoredCart) => {
          setSelectedValue(option);
          if (onSelected) {
            onSelected(option);
          }
        }}
        startIcon={startIcon}
        noSelectionLabel={titleId}
        selectOptions={LastActionsSelectOptions({
          repositoryOptions: storedCarts,
          renderDisplayElement: (item) => (
            <CartSelectorOption
              key={`${item.id}-${item.createdAt.getTime()}`}
              cart={item}
              removeCart={() => {
                setSelectedCartId(item.id);
                setOpened(true);
              }}
            />
          ),
          timeRanges: LAST_ACTIONS_TIME_SELECTORS,
          timeSelectionHeaderElement: (title) => <TimeSelectionHeader key={title} title={title} />,
        })}
      />
      {selectedCartId && opened && (
        <RemoveStoredCartDialog
          opened={opened}
          setOpened={setOpened}
          removeCartFromRepository={() => {
            setOpened(false);
            if (!selectedAgent || !cartRepository) return;
            cartRepository.remove(selectedAgent.id, selectedCartId);
            updateStoredCarts();
          }}
          selectedCart={storedCarts.find((cart) => cart.id === selectedCartId)!}
        />
      )}
    </>
  );
};
